import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CatButton } from "@cat-digital-blocks/react/bedrock/components/Button";
import { CatInputField } from "@cat-digital-blocks/react/bedrock/components/InputField";
import { CatInlineNotification } from "@cat-digital-blocks/react/bedrock/components/InlineNotification";
import { CatHeading } from "@cat-digital-blocks/react/bedrock/components/Heading";
import { CatTextareaField } from "@cat-digital-blocks/react/bedrock/components/TextareaField";
import { CatCheckboxField } from "@cat-digital-blocks/react/bedrock/components/CheckboxField";
import {
  useUpdateAttributeMappingMutation,
  useGetOperatorsByDataTypeQuery,
} from "@services/dcmApi";
import type {
  AttributeMappingRequest,
  AttributeMappingResponse,
  OperatorResponse,
} from "@/types/api";
import styles from "./AttributeRegistration.module.scss";

interface EditAttributeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  attribute: AttributeMappingResponse;
  userName?: string;
}

const EditAttributeModal = ({
  isOpen,
  onClose,
  onSuccess,
  attribute,
  userName,
}: EditAttributeModalProps) => {
  const { t } = useTranslation();

  const [mappingColumn, setMappingColumn] = useState(
    attribute.mappingColumn || "",
  );
  const [parserColumn, setParserColumn] = useState(
    attribute.parserColumn || "",
  );
  const [description, setDescription] = useState(attribute.description || "");
  const [dataType, setDataType] = useState(attribute.dataType || "");
  const [allowedOperators, setAllowedOperators] = useState<string[]>(
    attribute.allowedOperators || [],
  );
  const [allowedValues, setAllowedValues] = useState<string[]>(
    attribute.allowedValues || [],
  );
  const [newValue, setNewValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Fetch operators by data type
  const { data: availableOperators = [] } = useGetOperatorsByDataTypeQuery(
    { dataType: dataType || undefined },
    { skip: !dataType },
  );

  const [updateAttribute, { isLoading }] = useUpdateAttributeMappingMutation();

  const handleParserColumnChange = useCallback((event: CustomEvent) => {
    setParserColumn(event.detail?.value ?? "");
  }, []);

  const handleDescriptionChange = useCallback((event: CustomEvent) => {
    setDescription(event.detail?.value ?? "");
  }, []);

  // Handle operator checkbox toggle
  const handleOperatorToggle = useCallback(
    (operator: string, checked: boolean) => {
      setError(null);
      if (checked) {
        setAllowedOperators((prev) => [...prev, operator]);
      } else {
        setAllowedOperators((prev) => prev.filter((op) => op !== operator));
      }
    },
    [],
  );

  const handleAddValue = useCallback(() => {
    if (newValue.trim() && !allowedValues.includes(newValue.trim())) {
      setAllowedValues([...allowedValues, newValue.trim()]);
      setNewValue("");
    }
  }, [newValue, allowedValues]);

  const handleRemoveValue = useCallback((value: string) => {
    setAllowedValues((prev) => prev.filter((v) => v !== value));
  }, []);

  const validateForm = useCallback((): boolean => {
    return true;
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;
    try {
      const request: AttributeMappingRequest = {
        mappingColumn: mappingColumn.trim() || undefined,
        parserColumn: parserColumn.trim() || undefined,
        description: description.trim() || undefined,
        dataType: dataType || undefined,
        allowedOperators:
          allowedOperators.length > 0 ? allowedOperators : undefined,
        allowedValues: allowedValues.length > 0 ? allowedValues : undefined,
      };
      await updateAttribute({
        attributeId: attribute.id,
        body: request,
        userName,
      }).unwrap();
      onSuccess();
    } catch (err: unknown) {
      if (
        err &&
        typeof err === "object" &&
        "status" in err &&
        err.status === 409
      ) {
        setError(t("admin.attributeRegistration.error.duplicate"));
      } else {
        setError(t("admin.attributeRegistration.error.updateFailed"));
      }
    }
  }, [
    validateForm,
    mappingColumn,
    parserColumn,
    description,
    dataType,
    allowedOperators,
    allowedValues,
    updateAttribute,
    attribute.id,
    userName,
    onSuccess,
    t,
  ]);

  const handleClose = useCallback(() => {
    setMappingColumn("");
    setParserColumn("");
    setDescription("");
    setDataType("");
    setAllowedOperators([]);
    setAllowedValues([]);
    setNewValue("");
    setError(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <CatHeading variant="title-sm">
            {t("admin.attributeRegistration.editModal.title")}
          </CatHeading>
        </div>
        <div className={styles.modalBody}>
          {error && (
            <CatInlineNotification
              variant="error"
              className={styles.errorMessage}
            >
              {error}
            </CatInlineNotification>
          )}

          {/* Data Source (read-only) */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.attributeRegistration.createModal.dataSource")} *
            </label>
            <CatInputField
              label=""
              value={attribute.sourceName}
              disabled={true}
            />
          </div>

          {/* Mapping Column (read-only) */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.attributeRegistration.editModal.mappingColumn")} *
            </label>
            <CatInputField
              label=""
              value={mappingColumn}
              placeholder={t(
                "admin.attributeRegistration.createModal.mappingColumnPlaceholder",
              )}
              disabled={true}
              readonly
            />
          </div>

          {/* Parser Column (editable) & Data Type (read-only) */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>
                {t("admin.attributeRegistration.createModal.parserColumn")} *
              </label>
              <CatInputField
                label=""
                value={parserColumn}
                onBlChange={handleParserColumnChange}
                placeholder={t(
                  "admin.attributeRegistration.createModal.parserColumnPlaceholder",
                )}
                disabled={isLoading}
              />
            </div>
            <div className={styles.formGroup}>
              <label>
                {t("admin.attributeRegistration.createModal.dataType")} *
              </label>
              <CatInputField
                label=""
                value={dataType}
                placeholder={t(
                  "admin.attributeRegistration.createModal.dataTypePlaceholder",
                )}
                disabled={true}
                readonly
              />
            </div>
          </div>

          {/* Description */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.attributeRegistration.createModal.description")} *
            </label>
            <div className={styles.noLabelField}>
              <CatTextareaField
                label=" "
                value={description}
                onBlChange={handleDescriptionChange}
                placeholder={t(
                  "admin.attributeRegistration.createModal.descriptionPlaceholder",
                )}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Allowed Operators (checkboxes based on data type) */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.attributeRegistration.createModal.allowedOperators")} *
            </label>
            {!dataType ? (
              <div className={styles.fieldHint}>
                {t(
                  "admin.attributeRegistration.createModal.selectColumnForOperators",
                )}
              </div>
            ) : availableOperators.length === 0 ? (
              <div className={styles.fieldHint}>
                {t(
                  "admin.attributeRegistration.createModal.noOperatorsForType",
                )}
              </div>
            ) : (
              <div className={styles.operatorCheckboxes}>
                {availableOperators.map((opResponse: OperatorResponse) => {
                  const op = opResponse.operator || "";
                  if (!op) return null;
                  const isChecked = allowedOperators.includes(op);
                  return (
                    <CatCheckboxField
                      key={op}
                      value={op}
                      checked={isChecked}
                      onBlCheckboxChange={(e: CustomEvent) => {
                        const checked = e.detail?.checked ?? false;
                        handleOperatorToggle(op, checked);
                      }}
                      disabled={isLoading}
                      hasHover
                    >
                      {op}
                    </CatCheckboxField>
                  );
                })}
              </div>
            )}
          </div>

          {/* Allowed Values */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.attributeRegistration.createModal.allowedValues")}
            </label>
            <div className={styles.tagInput}>
              <CatInputField
                label=""
                value={newValue}
                onBlChange={(e: CustomEvent) =>
                  setNewValue(e.detail?.value ?? "")
                }
                placeholder={t(
                  "admin.attributeRegistration.createModal.valuePlaceholder",
                )}
                disabled={isLoading}
              />
              <CatButton
                variant="ghost"
                size="sm"
                onClick={handleAddValue}
                disabled={isLoading || !newValue.trim()}
              >
                {t("common.add")}
              </CatButton>
            </div>
            {allowedValues.length > 0 && (
              <div className={styles.tagContainer}>
                {allowedValues.map((val) => (
                  <span key={val} className={styles.tag}>
                    {val}
                    <span
                      className={styles.tagRemove}
                      onClick={() => handleRemoveValue(val)}
                    >
                      ×
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={styles.modalActions}>
          <CatButton
            variant="contrast"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("common.cancel")}
          </CatButton>
          <CatButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? t("common.saving") : t("common.update")}
          </CatButton>
        </div>
      </div>
    </div>
  );
};

export default EditAttributeModal;
